"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const lodash_1 = require("lodash");
const useDeepMemo_1 = require("../../hooks/useDeepMemo");
const theme_1 = require("../../theme");
const common_styled_components_1 = require("../common-styled-components");
const withParentDimensions_1 = require("../generic/withParentDimensions");
const d3_shape_1 = require("d3-shape");
const d3_scale_1 = require("d3-scale");
const styled_components_1 = __importDefault(require("styled-components"));
const react_1 = require("react");
const amountUtils_1 = require("../../util/amountUtils");
const ChartContainer = styled_components_1.default.div `
  width: 100%;
  margin-top: ${(p) => (p.noMargin ? 0 : theme_1.theme.spacing(4))};
  margin-bottom: ${(p) => (p.noMargin ? 0 : theme_1.theme.spacing(5))};
  overflow: visible;
`;
const START_AND_END_YEARS = [2025, 2050];
const getTickDigits = (maxValue) => {
    if (maxValue === undefined) {
        return 0;
    }
    if (maxValue < 1) {
        return 2;
    }
    if (maxValue < 10) {
        return 1;
    }
    return 0;
};
const getLocaleNumber = (digits, n) => n !== undefined
    ? n.toLocaleString("en-US", {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    })
    : undefined;
const WIDE_GRAPH_LIMIT = 420;
const MAX_WIDTH = 640;
exports.default = (0, withParentDimensions_1.withParentDimensions)(({ title, subTitle, unit, data, complianceData, chartType, forceZero, forcedMaxValue, forceDigits, parentDimensions, errorYearMessage, onChange, noMargin, canDivide, }) => {
    const [hoverYearIndex, setHoverYearIndex] = (0, react_1.useState)();
    const width = Math.min(parentDimensions.width, MAX_WIDTH);
    const isWide = width > WIDE_GRAPH_LIMIT;
    const heightWithoutTopMargin = 140;
    const fontSize = theme_1.theme.fontSizeNumber(-1);
    const tickFontSize = theme_1.theme.fontSizeNumber(-2);
    const topMargin = isWide
        ? Math.max(data.length * (fontSize + 2) + 6, 24)
        : 24;
    const height = heightWithoutTopMargin + topMargin;
    const margins = {
        top: isWide ? Math.max(data.length * (fontSize + 2) + 6, 24) : 24,
        bottom: 24,
        left: 70,
        right: 15,
    };
    const lineData = data.map((series) => ({
        ...series,
        values: series.values.length && chartType === "stepAfter"
            ? series.values.concat({
                year: series.values[series.values.length - 1].year + 5,
                value: series.values[series.values.length - 1].value,
            })
            : series.values,
    }));
    const innerPadding = isWide ? 25 : 15;
    const [allYearsWithData, divider, xScale, yScale, digits] = (0, useDeepMemo_1.useDeepMemo)(() => {
        const allValues = (0, lodash_1.chain)(data)
            .flatMap((series) => series.values)
            .map((d) => d.value)
            .valueOf();
        const allYears = (0, lodash_1.chain)(data)
            .flatMap((series) => series.values)
            .map((d) => d.year)
            .uniq()
            .valueOf();
        const maxValue = forcedMaxValue ?? (0, lodash_1.max)(allValues) ?? 1000;
        const minValue = (0, lodash_1.min)(allValues) ?? 0;
        const divider = canDivide ? (0, amountUtils_1.getDivider)(maxValue) ?? 1 : 1;
        const minMaxWithDivider = [minValue / divider, maxValue / divider];
        const scaleY = (0, d3_scale_1.scaleLinear)()
            .range([height - margins.bottom, margins.top])
            .domain([forceZero ? 0 : minMaxWithDivider[0], minMaxWithDivider[1]]);
        const scaleX = (0, d3_scale_1.scaleLinear)()
            .range([
            margins.left + innerPadding,
            width - margins.right - innerPadding,
        ])
            .domain([0, allYears.length - 1]);
        const digits = forceDigits ?? getTickDigits(minMaxWithDivider[1]);
        return [allYears, divider, scaleX, scaleY, digits];
    }, [
        data,
        canDivide,
        forceZero,
        forcedMaxValue,
        forceDigits,
        width,
        height,
    ]);
    if (width <= 0) {
        return (0, jsx_runtime_1.jsx)(ChartContainer, {});
    }
    const curve = chartType === "stepAfter" ? d3_shape_1.curveStepAfter : d3_shape_1.curveLinear;
    const lineGenerator = (0, d3_shape_1.line)()
        .x((_d, i) => Math.min(xScale(i), width - margins.right))
        .y((d) => yScale(d.value / divider))
        .curve(curve);
    const xTicks = allYearsWithData;
    const bandWidth = xScale(xTicks[1]) - xScale(xTicks[0]);
    const missingYearIndexes = complianceData?.reduce((result, cYear, i) => {
        if (!cYear.is_compliant) {
            return [...result, i];
        }
        return result;
    }, []);
    const missingYearsCenter = missingYearIndexes?.length
        ? (Math.max(xScale(missingYearIndexes[0]), 0) +
            Math.min(xScale(missingYearIndexes[missingYearIndexes.length - 1]) +
                bandWidth, width - margins.right)) /
            2
        : undefined;
    const bandScaleWidth = data.length * 4;
    const bandScale = (0, d3_scale_1.scaleBand)()
        .range([-bandScaleWidth, bandScaleWidth])
        .domain(data.map((d) => d.name));
    const hoverRender = () => {
        if (hoverYearIndex === undefined || isWide) {
            return undefined;
        }
        const hoveredDatum = data
            .map((s) => {
            const datum = s.values.find((d, i) => i === hoverYearIndex);
            return {
                name: s.name,
                color: s.color,
                value: datum?.value,
            };
        })
            .filter((d) => d.value !== undefined);
        if (!hoveredDatum.length) {
            return undefined;
        }
        const x = xScale(hoverYearIndex);
        const boxTop = margins.top;
        const numbersLength = Math.max(...hoveredDatum.map((d) => getLocaleNumber(digits, d.value)?.length ?? 0));
        const padding = 4;
        const boxWidth = numbersLength * 9 + 2 * padding;
        const left = width - x < boxWidth;
        return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("rect", { fill: theme_1.theme.colors.white, stroke: theme_1.theme.colors.grey(3), x: left ? x - boxWidth - 10 : x + 10, y: margins.top, width: boxWidth, height: hoveredDatum.length * (tickFontSize + 2) + padding * 2 }), hoveredDatum.map((d, i) => {
                    const circleOffset = bandScale(d.name);
                    return ((0, jsx_runtime_1.jsxs)("g", { children: [d.value !== undefined && ((0, jsx_runtime_1.jsx)("circle", { cx: x +
                                    (chartType === "bar" && circleOffset !== undefined
                                        ? circleOffset + bandScale.bandwidth() / 2
                                        : 0), cy: yScale(d.value / divider), r: 5, fill: d.color })), (0, jsx_runtime_1.jsx)("text", { x: x + (left ? -14 : 14), y: boxTop + padding + tickFontSize + (tickFontSize + 2) * i, fontSize: tickFontSize, fontWeight: "bold", fill: d.color, textAnchor: left ? "end" : "start", children: getLocaleNumber(digits, d.value !== undefined ? d.value / divider : undefined) })] }, d.name + i));
                })] }));
    };
    return ((0, jsx_runtime_1.jsxs)(ChartContainer, { noMargin: noMargin, children: [title ? (0, jsx_runtime_1.jsx)(common_styled_components_1.GraphTitle, { children: title }) : undefined, subTitle ? (0, jsx_runtime_1.jsx)(common_styled_components_1.GraphSubTitle, { children: subTitle }) : undefined, (0, jsx_runtime_1.jsxs)("svg", { width: width, height: height, style: { marginTop: theme_1.theme.spacing(0), overflow: "visible" }, children: [(0, jsx_runtime_1.jsx)("line", { x1: margins.left, x2: margins.left, y1: 0, y2: height - margins.bottom, stroke: theme_1.theme.colors.grey(4), strokeWidth: 1 }), unit ? ((0, jsx_runtime_1.jsxs)(common_styled_components_1.GraphLabel, { fontSize: fontSize, x: margins.left - 5, y: fontSize, textAnchor: "end", children: [(0, amountUtils_1.getDividerLabel)(divider), unit] })) : undefined, (0, jsx_runtime_1.jsx)("g", { children: xTicks.map((t, i) => {
                            const x = xScale(i);
                            return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: x, x2: x, y1: margins.top, y2: height - margins.bottom + 4, stroke: theme_1.theme.colors.grey(4), strokeWidth: 1 }), (0, jsx_runtime_1.jsx)("text", { fontSize: tickFontSize, textAnchor: "middle", x: x, y: height - margins.bottom + 4 + tickFontSize, children: t.toFixed(0) })] }, t));
                        }) }), (0, jsx_runtime_1.jsx)("g", { children: yScale.ticks(3).map((t) => {
                            const y = yScale(t);
                            return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: margins.left - 2, x2: width - margins.right, y1: y, y2: y, stroke: t === 0 ? theme_1.theme.colors.grey(1) : theme_1.theme.colors.grey(4), strokeWidth: 1 }), (0, jsx_runtime_1.jsx)("text", { fontSize: tickFontSize, textAnchor: "end", x: margins.left - 4, y: y + tickFontSize * 0.35, children: getLocaleNumber(digits, t) })] }, t));
                        }) }), chartType !== "bar" && ((0, jsx_runtime_1.jsxs)("g", { children: [lineData.map((series) => ((0, jsx_runtime_1.jsx)("g", { children: (0, jsx_runtime_1.jsx)("path", { d: lineGenerator(series.values) || "", fill: "none", strokeWidth: 2, stroke: series.color }) }, series.name))), data.map((series) => !series.hideCircles && ((0, jsx_runtime_1.jsx)("g", { children: series.values.map((v, i) => ((0, jsx_runtime_1.jsx)("circle", { cx: xScale(i), cy: yScale(v.value / divider), r: 4, fill: series.color }, v.year))) }, series.name)))] })), chartType === "bar" && ((0, jsx_runtime_1.jsx)("g", { children: data.map((series) => {
                            return ((0, jsx_runtime_1.jsx)("g", { children: series.values.map((bar, i) => {
                                    const offset = bandScale(series.name);
                                    const x = xScale(i) +
                                        (offset !== undefined
                                            ? offset + bandScale.bandwidth() / 2
                                            : 0);
                                    return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: x, x2: x, y1: height - margins.bottom, y2: yScale(bar.value / divider), stroke: series.color, strokeWidth: 3 }), (0, jsx_runtime_1.jsx)("circle", { cx: x, cy: yScale(bar.value / divider), r: 4, fill: series.color })] }, `${bar.year}-${series.name}-${i}`));
                                }) }, series.name));
                        }) })), isWide && ((0, jsx_runtime_1.jsx)("g", { children: data.map((series, seriesIndex) => {
                            return ((0, jsx_runtime_1.jsx)("g", { children: series.values.map((v, i) => {
                                    const x = xScale(i);
                                    return ((0, jsx_runtime_1.jsx)("text", { x: x, textAnchor: "middle", fill: series.color, y: (seriesIndex + 1) * fontSize + seriesIndex * 2, fontSize: fontSize, children: getLocaleNumber(digits, v.value / divider) }, v.year));
                                }) }, series.name));
                        }) })), missingYearIndexes?.length ? ((0, jsx_runtime_1.jsxs)("g", { children: [missingYearIndexes.map((i) => {
                                const x = xScale(i);
                                return ((0, jsx_runtime_1.jsx)("rect", { x: x, y: margins.top, width: x + bandWidth > width - margins.right
                                        ? width - margins.right - x
                                        : bandWidth, height: height - margins.bottom - margins.top, fill: "rgba(0,0,0,0.1)" }, i));
                            }), (0, jsx_runtime_1.jsx)("text", { x: missingYearsCenter, y: margins.top +
                                    (height - margins.bottom - margins.top) / 2 +
                                    theme_1.theme.fontSizeNumber(-1) / 2, fill: theme_1.theme.colors.grey(1), textAnchor: "middle", fontSize: theme_1.theme.fontSizeNumber(-1), children: errorYearMessage })] })) : undefined, hoverRender(), (0, jsx_runtime_1.jsx)("g", { children: bandWidth > 0 &&
                            xTicks.map((t, i) => ((0, jsx_runtime_1.jsx)("rect", { x: xScale(i) - bandWidth / 2, y: 0, width: bandWidth, height: height, fill: "rgba(0,0,0,0)", onMouseEnter: () => {
                                    setHoverYearIndex(i);
                                    if (onChange) {
                                        onChange(t);
                                    }
                                }, onMouseLeave: () => {
                                    setHoverYearIndex(undefined);
                                    if (onChange) {
                                        onChange(undefined);
                                    }
                                } }, t))) })] }), (0, jsx_runtime_1.jsx)(common_styled_components_1.Legend, { children: data.map((series) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.LegendItem, { color: series.color, text: series.name }, series.name))) })] }));
});
